import React, { useEffect, useMemo } from "react";
import { Location } from "@/types/trip";
import { Plus, Info, CircleAlert } from "lucide-react";
import { ServerFlightInfo, ServerRouteDetail } from "@/types/flights";
import { formatInTimeZone } from "date-fns-tz";
import { Tooltip } from "@heroui/tooltip";
import { DateTime } from "luxon";
import { FaExclamationCircle } from "react-icons/fa";
import { formatPrice } from "@/lib/general";
import { toast } from "@/hooks/use-toast";

interface FlightCardProps {
  flight: ServerFlightInfo;
  onSelect: (flight: ServerFlightInfo) => void;
  onAddFlight: (flight: ServerFlightInfo) => void;
  departureTimeZone: string;
  arrivalTimeZone: string;
  selectedLocation?: Location;
  selectedCurrencyCode: string;
  isAdmin: boolean;
}

const FlightCard: React.FC<FlightCardProps> = ({
  flight,
  onSelect,
  onAddFlight,
  departureTimeZone,
  arrivalTimeZone,
  selectedLocation,
  selectedCurrencyCode,
  isAdmin,
}) => {
  const firstLeg: ServerRouteDetail | undefined = flight.route[0];

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}hr ${mins}min`;
  };

  // Get unique airlines from the route
  const uniqueAirlines = Array.from(
    new Set(flight.route.map((leg) => leg.short_airline))
  );

  // Function to render stacked airline avatars
  const renderAirlineAvatars = () => {
    return uniqueAirlines.map((airline, index) => {
      const imageUrl = `https://images.kiwi.com/airlines/128x128/${airline}.png?default=airline.png`;
      return (
        <div
          key={airline}
          className="airline-avatar rounded-full border-2 border-white shadow-sm bg-white"
          style={{
            width: "2.5rem",
            height: "2.5rem",
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            marginLeft: index > 0 ? "-1rem" : "0",
            zIndex: uniqueAirlines.length - index,
          }}
        />
      );
    });
  };

  // selected location here is transport.selectedLocation !
  const isDateConflict = useMemo(() => {
    if (!flight || !selectedLocation) {
      return false;
    }

    // Convert location start date (UTC)
    const locationStartDate = DateTime.fromISO(
      selectedLocation!.date.start_date
    );

    // Convert flight arrival to destination timezone
    const flightArrivalDate = DateTime.fromISO(flight.arrival_utc).setZone(
      selectedLocation?.date.time_zone
    );

    // Compare the dates using toISODate() which gives YYYY-MM-DD without time/timezone
    const locationDate = locationStartDate.toISODate();
    const arrivalDate = flightArrivalDate.toISODate();

    return arrivalDate! > locationDate!;
  }, [flight, selectedLocation]);

  // Memoize the plane SVG to prevent unnecessary re-renders
  const PlaneSVG = useMemo(
    () => (
      <svg
        className="w-4 h-4 text-blue-600"
        stroke="currentColor"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 510 510"
      >
        <path d="M510 255c0-20.4-17.85-38.25-38.25-38.25H331.5L204 12.75h-51l63.75 204H76.5l-38.25-51H0L25.5 255 0 344.25h38.25l38.25-51h140.25l-63.75 204h51l127.5-204h140.25c20.4 0 38.25-17.85 38.25-38.25z" />
      </svg>
    ),
    []
  );

  // Cleanup SVG elements on unmount
  useEffect(() => {
    return () => {
      const svgElements = document.querySelectorAll("svg");
      svgElements.forEach((svg) => {
        const gradients = svg.querySelectorAll("linearGradient");
        gradients.forEach((gradient) => gradient.remove());
      });
    };
  }, []);

  const handleAddFlight = () => {
    if (!isAdmin) {
      toast({
        title: "Viewer access restricted",
        description: "Create a copy of this trip to add transport",
        duration: 3000,
      });
    } else {
      onAddFlight(flight);
    }
  };

  return (
    <div className="relative">
      {isDateConflict && (
        <div className="absolute top-2 right-2">
          <Tooltip
            className="shadow-lg ring-1 ring-neutral-200"
            placement="left"
            closeDelay={0}
            showArrow={true}
            content={
              <div className="flex flex-row items-center justify-center space-x-2 p-1">
                <FaExclamationCircle className="text-neutral-800" />
                <p className="text-sm font-light text-neutral-800">
                  This flight arrives after the set starting date of your next
                  location.
                </p>
              </div>
            }
          >
            <CircleAlert className="h-4 w-4 text-rose-500" />
          </Tooltip>
        </div>
      )}
      <div className="flight-option-view h-full p-3 w-full border group rounded-lg flex flex-col transition duration-300 ease-in-out">
        <div className="flex flex-row h-full">
          {/* MAIN */}
          <div className="w-[75%] flex flex-col">
            <div className="w-full flex flex-row items-center">
              <div className="flex flex-col justify-center pt-1 pb-1 pl-3 pr-3 bg-neutral-100 rounded-lg">
                <h1 className="flight-date-day-depart text-base text-neutral-700 font-semibold text-center">
                  {formatInTimeZone(
                    flight.departure_utc,
                    departureTimeZone,
                    "d"
                  )}
                </h1>
                <h1 className="flight-date-month-depart text-xs text-neutral-500 text-center">
                  {formatInTimeZone(
                    flight.departure_utc,
                    departureTimeZone,
                    "MMM"
                  )}
                </h1>
              </div>
              <div className="flex flex-col ml-2">
                <h1 className="flight-airline text-[17px] text-neutral-900 font-semibold">
                  {uniqueAirlines.length > 1
                    ? "Multiple Airlines"
                    : firstLeg?.airline}
                </h1>
                <div className="flex flex-row items-center justify-start mt-0.5">
                  <h1 className="flight-number text-xs font-normal text-neutral-500">
                    {firstLeg?.short_airline}
                    {firstLeg?.flight_number}
                    {uniqueAirlines.length > 1 && "+"}
                    {uniqueAirlines.length - 1}
                  </h1>
                  <div className="w-1 h-1 bg-neutral-300 rounded-full ml-2 mr-2"></div>
                  <p className="flight-duration text-neutral-500 font-light text-xs text-center">
                    {formatDuration(flight.duration_of_journey)}
                  </p>
                </div>
              </div>
              <div className="ml-auto mr-2 flex">{renderAirlineAvatars()}</div>
            </div>

            <div className="w-full bg-neutral-300 mt-3 h-[1px]"></div>

            {/* FLIGHT INFO */}
            <div className="w-full flex flex-row justify-between px-2.5 items-center mt-4 mb-1">
              <div className="flex-none flex flex-col justify-center items-center">
                <p className="flight-time-depart text-[17px] text-neutral-800 font-semibold text-center">
                  {DateTime.fromISO(flight.departure_utc)
                    .setZone(departureTimeZone)
                    .toFormat("HH:mm")}
                </p>
                <p className="flight-iata-depart text-xs text-neutral-400 text-center">
                  {flight.from.airport}
                </p>
              </div>

              {flight.stops === 0 ? (
                <div className="direct w-1/2 flex flex-col justify-center mt-2">
                  <div className="flex flex-row space-x-3 items-center justify-center mb-1">
                    <h2 className="px-2 rounded-full py-0.5 bg-neutral-100 text-[11px] text-neutral-600 font-semibold">
                      Direct
                    </h2>
                  </div>
                  <div className="flex flex-row items-center mt-2 mb-1">
                    <div className="bg-blue-600 rounded-full w-2 h-2"></div>
                    <div className="h-[2px] bg-neutral-300 flex-grow"></div>
                    <div className="flex-none flex items-center justify-center bg-white rounded-full mx-1">
                      {PlaneSVG}
                    </div>
                    <div className="h-[2px] bg-neutral-300 flex-grow"></div>
                    <div className="bg-blue-600 rounded-full w-2 h-2"></div>
                  </div>
                </div>
              ) : (
                <div className="non-direct w-1/2 flex flex-col justify-center mt-2">
                  <div className="flex flex-row space-x-4 items-center justify-center mb-1">
                    <h2 className="px-2 py-0.5 rounded-full bg-orange-100 text-[10px] text-orange-600 font-semibold">
                      {flight.stops} stop{flight.stops > 1 ? "s" : ""}
                    </h2>
                  </div>
                  <div className="flex flex-row items-center mt-2 mb-1">
                    <div className="bg-blue-600 rounded-full w-2 h-2"></div>
                    <div className="h-[2px] bg-neutral-300 flex-grow"></div>
                    <div className="flex flex-col">
                      <div className="h-3 w-3 border-1 border-white bg-orange-600 ml-[2px] mr-[2px] items-center justify-center rounded-full"></div>
                    </div>
                    <div className="h-[2px] bg-neutral-300 flex-grow"></div>
                    <div className="bg-blue-600 rounded-full w-2 h-2"></div>
                  </div>
                  <div className="flex flex-row space-x-3 items-center justify-center">
                    <div className="flex flex-col items-center">
                      <p className="text-orange-600 font-light text-xs text-center">
                        <span className="layover-stops font-semibold">
                          {flight.route[1]?.from.city}
                        </span>
                      </p>
                      <p className="layover-duration text-[11px] text-neutral-400 font-light">
                        {calculateLayoverDuration(flight).formatted}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex-none flex flex-col items-center">
                <p className="flight-time-arrive text-[17px] text-neutral-800 font-semibold text-center">
                  {formatInTimeZone(
                    flight.arrival_utc,
                    arrivalTimeZone,
                    "HH:mm"
                  )}
                </p>
                <p className="flight-iata-arrive text-xs text-neutral-400 text-center">
                  {flight.to.airport}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-neutral-300 w-[1px] mx-2"></div>

          <div className="flex flex-col justify-center items-center w-[20%]">
            <h2 className="text-xs text-neutral-400 text-center">
              Starting from
            </h2>
            <span className="flight-ticket-price text-lg text-neutral-800 mt-1 font-semibold text-center">
              {formatPrice(selectedCurrencyCode, flight.price)}
            </span>
            <h1 className="text-xs text-neutral-400">per person</h1>

            <div className="mt-3 flex flex-row items-center justify-between space-x-1.5">
              <button
                onClick={handleAddFlight}
                className={`flight-add-button flex items-center justify-center w-8 h-8 rounded-md bg-indigo-700 font-semibold text-white transition duration-300 ease-in-out hover:bg-indigo-800 ${
                  isAdmin ? "" : "opacity-50"
                }`}
              >
                <Plus size={16} />
              </button>
              <button
                onClick={() => onSelect(flight)}
                className="flight-info-button flex items-center justify-center w-8 h-8 rounded-md bg-neutral-200/75 text-base font-semibold italic text-neutral-700 transition duration-300 ease-in-out hover:bg-neutral-300"
              >
                <Info size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function calculateLayoverDuration(flight: ServerFlightInfo): {
  duration: number;
  formatted: string;
} {
  if (!flight.route || flight.route.length <= 1) {
    return { duration: 0, formatted: "0h 0m" };
  }

  const layovers: number[] = [];

  for (let i = 0; i < flight.route.length - 1; i++) {
    const currentFlight = flight.route[i]!;
    const nextFlight = flight.route[i + 1]!;

    const arrivalTime = new Date(currentFlight.arrival_utc);
    const departureTime = new Date(nextFlight.departure_utc);

    const layoverMinutes =
      (departureTime.getTime() - arrivalTime.getTime()) / 1000 / 60;
    layovers.push(layoverMinutes);
  }

  const totalLayoverMinutes = layovers.reduce(
    (sum, current) => sum + current,
    0
  );
  const hours = Math.floor(totalLayoverMinutes / 60);
  const minutes = Math.floor(totalLayoverMinutes % 60);

  return {
    duration: totalLayoverMinutes,
    formatted: `${hours}h ${minutes}m`,
  };
}

export default FlightCard;
